<template>
    <div class="fixed inset-0 bg-black/[0.04] flex items-center justify-center z-[1000] backdrop-blur-sm" 
         @click.self="handleBackdropClick">
        <div class="relative overflow-auto bg-n-alpha-3 shadow-xl rounded-xl rtl:text-right skip-context-menu w-[37.5rem] max-h-[80vh]">
            <div class="flex justify-between items-center p-4 border-b-2 border-n-slate-4 dark:border-n-slate-7">
                <h3 class="text-lg font-semibold m-0">{{ modalTitle }}</h3>
                <button
                    class="z-10 ltr:right-2 rtl:left-2 top-2 inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 text-n-slate-12 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent h-10 w-10 p-0 text-sm font-medium justify-center"
                    @click="closeModal">
                    <span class="i-lucide-x flex-shrink-0"></span>
                </button>
            </div>

            <div class="p-6">
                <!-- Form fields for add mode -->
                <template v-if="mode === 'add'">
                    <!-- Specialty Name -->
                    <div class="mb-5">
                        <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Nome da especialidade <span class="text-red-500">*</span></label>
                        <input 
                            type="text" 
                            v-model="formData.name" 
                            placeholder="Insira o nome da especialidade"
                            class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                            maxlength="255"
                            required 
                        />
                    </div>

                    <!-- Specialty Description -->
                    <div class="mb-5">
                        <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Descrição da especialidade</label>
                        <textarea 
                            v-model="formData.description" 
                            placeholder="Insira a descrição da especialidade (opcional)"
                            class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1 resize-y" 
                            rows="3"
                            maxlength="500"
                        ></textarea>
                        <div class="text-xs mt-1" :class="{'text-red-500': descriptionCharsRemaining < 50, 'text-n-slate-10': descriptionCharsRemaining >= 50}">
                            {{ descriptionCharsRemaining }} caracteres restantes
                        </div>
                    </div>

                    <!-- Error Message -->
                    <div v-if="error" class="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <p class="text-red-600 dark:text-red-400 text-sm">{{ error }}</p>
                    </div>
                </template>

                <!-- Delete confirmation -->
                <template v-if="mode === 'del'">
                    <div class="flex gap-4 items-start">
                        <div class="flex-1">
                            <p class="text-n-slate-12 text-sm leading-relaxed">
                                Tem certeza que deseja excluir a especialidade "<span class="font-medium">{{ specialty.name }}</span>"? 
                                <span class="block mt-2 text-n-slate-10">Esta ação não pode ser desfeita.</span>
                            </p>
                        </div>
                    </div>
                    
                    <!-- Error Message for delete -->
                    <div v-if="error" class="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <p class="text-red-600 dark:text-red-400 text-sm">{{ error }}</p>
                    </div>
                </template>
            </div>

            <div class="flex justify-end gap-3 p-4 border-t-2 border-n-slate-4 dark:border-n-slate-7 sticky bottom-0 bg-n-alpha-3">
                <button type="button"
                    class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
                    @click="closeModal" :disabled="loading">
                    Cancelar
                </button>
                <button type="button"
                    :class="actionButtonClass"
                    @click="handleAction" :disabled="actionButtonDisabled">
                    <span v-if="loading" class="animate-spin i-lucide-loader"></span>
                    <span>{{ actionButtonText }}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { debounce } from 'lodash';
import wootAPI from '@/v3/api/apiClient';

export default {
    name: 'GeneralSpecialtyModal',
    props: {
        mode: {
            type: String,
            required: true,
            validator: (value) => ['add', 'del'].includes(value)
        },
        specialty: {
            type: Object,
            default: () => null
        }
    },
    emits: ['close', 'save', 'delete'],
    setup(props, { emit }) {
        const formData = ref({
            name: '',
            description: ''
        });

        const loading = ref(false);
        const error = ref(null);

        // Computed properties
        const modalTitle = computed(() => {
            switch (props.mode) {
                case 'add': return 'Adicionar Especialidade';
                case 'del': return 'Excluir Especialidade';
                default: return '';
            }
        });

        const actionButtonClass = computed(() => {
            const baseClass = "inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 outline-transparent h-10 px-4 text-sm font-medium justify-center";
            
            if (props.mode === 'del') {
                return `${baseClass} bg-n-ruby-9 text-white hover:enabled:bg-n-ruby-10 focus-visible:bg-n-ruby-10`;
            }
            return `${baseClass} bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110`;
        });

        const actionButtonText = computed(() => {
            if (loading.value) {
                switch (props.mode) {
                    case 'add': return 'Adicionando...';
                    case 'del': return 'Excluindo...';
                    default: return 'Processando...';
                }
            }
            
            switch (props.mode) {
                case 'add': return 'Adicionar especialidade';
                case 'del': return 'Excluir especialidade';
                default: return '';
            }
        });

        const actionButtonDisabled = computed(() => {
            if (loading.value) return true;
            if (props.mode === 'del') return false;
            return !isFormValid.value;
        });

        const isFormValid = computed(() => {
            if (props.mode === 'del') return true;
            return formData.value.name && formData.value.name.trim() !== '';
        });

        const descriptionCharsRemaining = computed(() => {
            return 500 - (formData.value.description || '').length;
        });

        // Function to check if all form fields are empty
        const areAllFieldsEmpty = () => {
            return (
                formData.value.name.trim() === '' &&
                formData.value.description.trim() === ''
            );
        };

        // Function to handle backdrop click
        const handleBackdropClick = () => {
            if (props.mode === 'del' || areAllFieldsEmpty()) {
                closeModal();
            }
        };

        const closeModal = () => {
            emit('close');
        };

        // Clear error when user edits any field
        watch([
            () => formData.value.name,
            () => formData.value.description
        ], () => { error.value = null; });

        // Auth functions
        const getCookie = (name) => {
            const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
            return match ? decodeURIComponent(match[2]) : null;
        };

        const getAuthHeaders = () => {
            const cookie = getCookie('cw_d_session_info');
            if (!cookie) {
                console.error('cw_d_session_info cookie not found');
                return {};
            }
            try {
                const sessionInfo = JSON.parse(cookie);
                return {
                    'access-token': sessionInfo['access-token'],
                    'client': sessionInfo['client'],
                    'uid': sessionInfo['uid']
                };
            } catch (e) {
                console.error('Error parsing cw_d_session_info cookie:', e);
                return {};
            }
        };

        // Action handlers
        const handleAction = () => {
            switch (props.mode) {
                case 'add':
                    saveSpecialty();
                    break;
                case 'del':
                    deleteSpecialty();
                    break;
            }
        };

        const saveSpecialty = debounce(async () => {
            if (!isFormValid.value) return;

            loading.value = true;
            error.value = null;

            const headers = getAuthHeaders();
            if (!headers['access-token']) {
                error.value = 'Usuário não autenticado.';
                loading.value = false;
                return;
            }

            // Prepare data to save
            const specialtyData = {
                name: formData.value.name.trim(),
            };

            // Only include description if it has a value
            if (formData.value.description && formData.value.description.trim() !== '') {
                specialtyData.description = formData.value.description;
            }

            try {
                const response = await wootAPI.post(
                    `/api/v1/specialties`,
                    { specialty: specialtyData },
                    { headers }
                );

                const savedSpecialty = response.data;
                emit('save', savedSpecialty);
                resetForm();
                closeModal();
            } catch (err) {
                console.error('Failed to save specialty', err);
                error.value = err.response?.data?.message || 'Erro interno do servidor. Tente novamente.';
            } finally {
                loading.value = false;
            }
        }, 300);

        const deleteSpecialty = debounce(async () => {
            loading.value = true;
            error.value = null;

            const headers = getAuthHeaders();
            if (!headers['access-token']) {
                error.value = 'Usuário não autenticado.';
                loading.value = false;
                return;
            }

            try {
                await wootAPI.delete(
                    `/api/v1/specialties/${props.specialty.id}`,
                    { headers }
                );

                emit('delete', props.specialty);
                closeModal();
            } catch (err) {
                console.error('Erro ao excluir especialidade', err);
                error.value = err.response?.data?.message || 'Erro interno do servidor. Tente novamente.';
            } finally {
                loading.value = false;
            }
        }, 300);

        const resetForm = () => {
            formData.value = {
                name: '',
                description: ''
            };
            error.value = null;
        };

        return {
            formData,
            loading,
            error,
            modalTitle,
            actionButtonClass,
            actionButtonText,
            actionButtonDisabled,
            isFormValid,
            descriptionCharsRemaining,
            handleBackdropClick,
            areAllFieldsEmpty,
            closeModal,
            handleAction
        };
    }
}
</script>

<style scoped>
/* Minimal custom styles - most styling now handled by Tailwind utilities */

/* Screen reader only class for accessibility */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>